'use client';

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { featureFlags, FEATURE_FLAGS } from '@/lib/featureFlags';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Edit, MapPin } from 'lucide-react';
import { useClientProfile } from '@/hooks/useClientProfile';
import { useOrganizationProfile } from '@/hooks/useOrganizationProfile';
import { useToast } from '@/hooks/use-toast';
import { ReviewsSection } from '@/components/reviews/ReviewsSection';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { useIsOrganizationOwner } from '@/hooks/useIsOrganizationOwner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TaxExemptionsTab } from '@/components/settings/TaxExemptionsTab';
import { useAddressValidation } from '@/hooks/useAddressValidation';
import { AddressValidationBadge } from '@/components/shared/AddressValidationBadge';
import { AddressSuggestionDialog } from '@/components/shared/AddressSuggestionDialog';
import { addressDiffersFromStandardized } from '@/utils/addressUtils';

const businessTypes = [
  'construction',
  'manufacturing',
  'technology',
  'consulting',
  'design',
  'marketing',
  'logistics',
  'healthcare',
  'education',
  'finance',
  'other'
];

const employeeCountRanges = [
  '1-10',
  '11-50',
  '51-200',
  '201-1000',
  '1000+'
];

const revenueRanges = [
  'Under $1M',
  '$1M-$5M',
  '$5M-$25M',
  '$25M-$100M',
  'Over $100M'
];

export default function ClientProfilePage() {
  const { user, organization } = useAuth();
  const [searchParams] = useSearchParams();
  const {
    profile,
    completion,
    loading,
    createProfile,
    updateProfile,
  } = useClientProfile();

  const { 
    profile: orgProfile, 
    organizationName,
    loading: orgLoading, 
    createProfile: createOrgProfile, 
    updateProfile: updateOrgProfile 
  } = useOrganizationProfile();

  const isOwner = useIsOrganizationOwner();
  const { toast } = useToast();
  
  // Initialize activeTab from URL query parameter
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === 'company' ? 'company' : 'user');
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [showTaxExemptions, setShowTaxExemptions] = useState(true);

  // Check tax exemption feature flag
  useEffect(() => {
    const checkTaxExemptionFlag = async () => {
      const enabled = await featureFlags.isEnabled(FEATURE_FLAGS.TAX_EXEMPTION_MANAGEMENT);
      setShowTaxExemptions(enabled);
    };
    checkTaxExemptionFlag();
  }, []);

  // Address validation
  const { validateAddress, isValidating, lastResult, clearValidation } = useAddressValidation();
  const [addressValidated, setAddressValidated] = useState(false);
  const [addressDeliverability, setAddressDeliverability] = useState<string | null>(null);

  const [userFormData, setUserFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    timezone: '',
    job_title: '',
    department: '',
  });

  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    industry: '',
    founded_year: '',
    company_size: '',
    annual_revenue_range: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    website_url: '',
    accounts_payable_email: '',
    description: '',
  });

  const handleUserProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (profile) {
        await updateProfile({
          first_name: userFormData.first_name,
          last_name: userFormData.last_name,
          phone_number: userFormData.phone_number,
          job_title: userFormData.job_title,
          department: userFormData.department,
          timezone: userFormData.timezone,
        });
      } else {
        await createProfile({
          first_name: userFormData.first_name,
          last_name: userFormData.last_name,
          phone_number: userFormData.phone_number,
          job_title: userFormData.job_title,
          department: userFormData.department,
          timezone: userFormData.timezone,
        });
      }
      setIsEditingUser(false);
    } catch (error) {
      console.error('Failed to save user profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleValidateAndSaveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If address fields are filled, validate first
    if (companyFormData.street_address && companyFormData.city && companyFormData.state && companyFormData.zip_code) {
      const result = await validateAddress({
        street: companyFormData.street_address,
        city: companyFormData.city,
        state: companyFormData.state,
        zipcode: companyFormData.zip_code,
      });

      if (result.isValid && result.standardizedAddress) {
        // Check if standardized address differs from user input
        const addressDiffers = addressDiffersFromStandardized(
          {
            street: companyFormData.street_address,
            city: companyFormData.city,
            state: companyFormData.state,
            zipcode: companyFormData.zip_code,
          },
          result.standardizedAddress
        );

        if (addressDiffers) {
          // Show dialog to let user choose
          setShowSuggestionDialog(true);
          return;
        }

        // Address matches exactly - save directly
        setAddressValidated(true);
        setAddressDeliverability(result.deliverability);
        await saveCompanyProfile(companyFormData, true, result.deliverability);
      } else if (result.suggestions && result.suggestions.length > 0) {
        // Show suggestion dialog
        setShowSuggestionDialog(true);
        return;
      } else {
        // No valid result, but allow saving with warning
        await saveCompanyProfile(companyFormData, false, null);
      }
    } else {
      // No address to validate, just save
      await saveCompanyProfile(companyFormData, false, null);
    }
  };

  const handleSelectSuggestedAddress = async (address: any) => {
    const updatedData = {
      ...companyFormData,
      street_address: address.street,
      city: address.city,
      state: address.state,
      zip_code: address.zipcode,
    };
    setCompanyFormData(updatedData);
    setAddressValidated(true);
    setAddressDeliverability('deliverable');
    setShowSuggestionDialog(false);
    await saveCompanyProfile(updatedData, true, 'deliverable');
  };

  const handleKeepOriginalAddress = async () => {
    setShowSuggestionDialog(false);
    await saveCompanyProfile(companyFormData, false, null);
  };

  const saveCompanyProfile = async (
    data: typeof companyFormData, 
    validated: boolean, 
    deliverability: string | null
  ) => {
    try {
      // Get user's organization ID
      const { data: { user } } = await supabase.auth.getUser();
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user?.id)
        .single();

      if (!userProfile?.organization_id) {
        throw new Error('No organization found for user');
      }

      // 1. Update organization name in organizations table
      if (data.name) {
        const { error: orgError } = await supabase
          .from('organizations')
          .update({ name: data.name })
          .eq('id', userProfile.organization_id);
        
        if (orgError) {
          console.error('Failed to update organization name:', orgError);
          throw orgError;
        }
      }

      // 2. Update/create organization profile
      const orgProfileData = {
        industry: data.industry,
        founded_year: data.founded_year ? parseInt(data.founded_year) : null,
        company_size: data.company_size,
        annual_revenue_range: data.annual_revenue_range,
        street_address: data.street_address,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code,
        country: data.country,
        website_url: data.website_url?.trim() && !data.website_url.trim().match(/^https?:\/\//i)
          ? `https://${data.website_url.trim()}`
          : data.website_url?.trim() || '',
        accounts_payable_email: data.accounts_payable_email,
        description: data.description,
        address_validated: validated,
        address_validated_at: validated ? new Date().toISOString() : null,
        address_deliverability: deliverability,
      };

      if (orgProfile) {
        await updateOrgProfile(orgProfileData);
      } else {
        await createOrgProfile(orgProfileData);
      }
      
      setIsEditingCompany(false);
      toast({
        title: "Success",
        description: "Company profile updated successfully",
      });
    } catch (error: any) {
      console.error('Failed to save company profile:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update company profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleManualValidation = async () => {
    if (companyFormData.street_address && companyFormData.city && companyFormData.state && companyFormData.zip_code) {
      const result = await validateAddress({
        street: companyFormData.street_address,
        city: companyFormData.city,
        state: companyFormData.state,
        zipcode: companyFormData.zip_code,
      });

      // Check if standardized address differs from user input
      const addressDiffers = addressDiffersFromStandardized(
        {
          street: companyFormData.street_address,
          city: companyFormData.city,
          state: companyFormData.state,
          zipcode: companyFormData.zip_code,
        },
        result.standardizedAddress
      );

      const hasSuggestions = result.standardizedAddress || 
        (result.suggestions && result.suggestions.length > 0);

      // Show dialog if address was corrected OR if invalid with suggestions
      if (addressDiffers || (hasSuggestions && !result.isValid)) {
        setShowSuggestionDialog(true);
      } else if (result.isValid) {
        // Address matches - apply and mark as validated
        if (result.standardizedAddress) {
          setCompanyFormData(prev => ({
            ...prev,
            street_address: result.standardizedAddress!.street,
            city: result.standardizedAddress!.city,
            state: result.standardizedAddress!.state,
            zip_code: result.standardizedAddress!.zipcode,
          }));
        }
        setAddressValidated(true);
        setAddressDeliverability(result.deliverability);
      }
    }
  };

  // Clear validation when address fields change
  const handleAddressFieldChange = (field: string, value: string) => {
    setCompanyFormData(prev => ({ ...prev, [field]: value }));
    setAddressValidated(false);
    setAddressDeliverability(null);
    clearValidation();
  };

  // Load user profile data when available
  React.useEffect(() => {
    const loadUserData = async () => {
      setUserFormData({
        first_name: profile?.first_name || '',
        last_name: profile?.last_name || '',
        email: user?.email || '',
        phone_number: profile?.phone_number || '',
        timezone: profile?.timezone || '',
        job_title: profile?.job_title || '',
        department: profile?.department || '',
      });
    };
    loadUserData();
  }, [profile, user]);

  // Load company profile data from organization when available
  React.useEffect(() => {
    const loadOrgData = async () => {
      // Fetch organization name from organizations table
      const { data: { user } } = await supabase.auth.getUser();
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user?.id)
        .single();
      
      let orgName = '';
      if (userProfile?.organization_id) {
        const { data: org } = await supabase
          .from('organizations')
          .select('name')
          .eq('id', userProfile.organization_id)
          .single();
        
        orgName = org?.name || '';
      }
      
      setCompanyFormData({
        name: orgName,
        industry: orgProfile?.industry || '',
        founded_year: orgProfile?.founded_year?.toString() || '',
        company_size: orgProfile?.company_size || '',
        annual_revenue_range: orgProfile?.annual_revenue_range || '',
        street_address: orgProfile?.street_address || '',
        city: orgProfile?.city || '',
        state: orgProfile?.state || '',
        zip_code: orgProfile?.zip_code || '',
        country: orgProfile?.country || '',
        website_url: orgProfile?.website_url || '',
        accounts_payable_email: orgProfile?.accounts_payable_email || '',
        description: orgProfile?.description || '',
      });
    };
    loadOrgData();
  }, [orgProfile, user]);

  if (loading || orgLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Client Profile{organization?.name ? ` - ${organization.name}` : ''}</h1>
          <p className="text-muted-foreground">
            Manage your profile and company information
          </p>
        </div>
      </div>

      {/* Profile Completion Progress */}
      {completion && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Profile Completion
              <Badge variant="outline">{completion.overall_percentage}%</Badge>
            </CardTitle>
            <CardDescription>
              Complete your profile to improve your experience
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={completion.overall_percentage} className="mb-4" />
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className={`flex items-center gap-2 ${completion.basic_info_complete ? 'text-green-600' : 'text-muted-foreground'}`}>
                <div className={`w-2 h-2 rounded-full ${completion.basic_info_complete ? 'bg-green-500' : 'bg-gray-300'}`} />
                Basic Info
              </div>
              <div className={`flex items-center gap-2 ${completion.procurement_complete ? 'text-green-600' : 'text-muted-foreground'}`}>
                <div className={`w-2 h-2 rounded-full ${completion.procurement_complete ? 'bg-green-500' : 'bg-gray-300'}`} />
                Company Profile
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="user">User Profile</TabsTrigger>
          <TabsTrigger value="company">Company Profile</TabsTrigger>
        </TabsList>

        {/* User Profile Tab */}
        <TabsContent value="user">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Basic Information
                  </CardTitle>
                  <CardDescription>
                    Manage your personal profile information
                  </CardDescription>
                </div>
                {!isEditingUser && (
                  <Button
                    onClick={() => setIsEditingUser(true)}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleUserProfileSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={userFormData.first_name}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder="John"
                      disabled={!isEditingUser}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={userFormData.last_name}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder="Doe"
                      disabled={!isEditingUser}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={userFormData.email}
                      placeholder="john.doe@example.com"
                      disabled
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      To change your email, visit{' '}
                      <a href="/settings/account" className="text-primary hover:underline font-medium">
                        Account Settings
                      </a>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone_number">Phone Number *</Label>
                    <Input
                      id="phone_number"
                      type="tel"
                      value={userFormData.phone_number}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                      placeholder="+1 (555) 123-4567"
                      disabled={!isEditingUser}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Select
                      value={userFormData.timezone}
                      onValueChange={(value) => setUserFormData(prev => ({ ...prev, timezone: value }))}
                      disabled={!isEditingUser}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EST">Eastern Time (EST)</SelectItem>
                        <SelectItem value="CST">Central Time (CST)</SelectItem>
                        <SelectItem value="MST">Mountain Time (MST)</SelectItem>
                        <SelectItem value="PST">Pacific Time (PST)</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job_title">Job Title *</Label>
                    <Input
                      id="job_title"
                      value={userFormData.job_title}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, job_title: e.target.value }))}
                      placeholder="Procurement Manager"
                      disabled={!isEditingUser}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={userFormData.department}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, department: e.target.value }))}
                      placeholder="Procurement"
                      disabled={!isEditingUser}
                    />
                  </div>
                </div>

                {isEditingUser && (
                  <div className="flex gap-4 pt-4">
                    <Button type="submit">
                      Update Profile
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditingUser(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Reviews Section */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Reviews</CardTitle>
              <CardDescription>
                Feedback from vendors you've worked with
              </CardDescription>
            </CardHeader>
            <CardContent>
              {user?.id && <ReviewsSection userId={user.id} userType="client" />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Company Profile Tab */}
        <TabsContent value="company">
          {!isOwner && (
            <Alert className="mb-4">
              <AlertDescription>
                Company information is managed by the organization owner. Only the owner can edit these details. To change company information, contact your organization owner or visit{' '}
                <a href="/settings/organization" className="text-primary hover:underline font-medium">
                  Organization Settings
                </a> if you have owner access.
              </AlertDescription>
            </Alert>
          )}
          
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Company Overview</CardTitle>
                  <CardDescription>
                    {isOwner ? 'Manage your organization information' : 'View your organization information'}
                  </CardDescription>
                </div>
                {isOwner && !isEditingCompany && (
                  <Button
                    onClick={() => setIsEditingCompany(true)}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleValidateAndSaveCompany} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Organization Name *</Label>
                    <Input
                      id="name"
                      value={companyFormData.name}
                      onChange={(e) => setCompanyFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your company name"
                      disabled={!isEditingCompany}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="industry">Business Type *</Label>
                    <Select
                      value={companyFormData.industry}
                      onValueChange={(value) => setCompanyFormData(prev => ({ ...prev, industry: value }))}
                      disabled={!isEditingCompany}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select business type" />
                      </SelectTrigger>
                      <SelectContent>
                        {businessTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="founded_year">Founded Year *</Label>
                    <Input
                      id="founded_year"
                      type="number"
                      value={companyFormData.founded_year}
                      onChange={(e) => setCompanyFormData(prev => ({ ...prev, founded_year: e.target.value }))}
                      placeholder="2020"
                      min="1900"
                      max={new Date().getFullYear()}
                      disabled={!isEditingCompany}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_size">Employee Count Range *</Label>
                    <Select
                      value={companyFormData.company_size}
                      onValueChange={(value) => setCompanyFormData(prev => ({ ...prev, company_size: value }))}
                      disabled={!isEditingCompany}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee count" />
                      </SelectTrigger>
                      <SelectContent>
                        {employeeCountRanges.map((range) => (
                          <SelectItem key={range} value={range}>
                            {range}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="annual_revenue_range">Annual Revenue Range</Label>
                    <Select
                      value={companyFormData.annual_revenue_range}
                      onValueChange={(value) => setCompanyFormData(prev => ({ ...prev, annual_revenue_range: value }))}
                      disabled={!isEditingCompany}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select revenue range" />
                      </SelectTrigger>
                      <SelectContent>
                        {revenueRanges.map((range) => (
                          <SelectItem key={range} value={range}>
                            {range}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="street_address">Street Address *</Label>
                      <AddressValidationBadge
                        isValidated={addressValidated || (orgProfile as any)?.address_validated}
                        deliverability={(addressDeliverability || (orgProfile as any)?.address_deliverability) as any}
                        onVerify={isEditingCompany ? handleManualValidation : undefined}
                        isVerifying={isValidating}
                        compact
                      />
                    </div>
                    <Input
                      id="street_address"
                      value={companyFormData.street_address}
                      onChange={(e) => handleAddressFieldChange('street_address', e.target.value)}
                      placeholder="123 Main Street"
                      disabled={!isEditingCompany}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={companyFormData.city}
                      onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                      placeholder="New York"
                      disabled={!isEditingCompany}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">State/Province *</Label>
                    <Select
                      value={companyFormData.state}
                      onValueChange={(v) => handleAddressFieldChange('state', v)}
                      disabled={!isEditingCompany}
                    >
                      <SelectTrigger id="state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <SelectItem value="AL">Alabama (AL)</SelectItem>
                        <SelectItem value="AK">Alaska (AK)</SelectItem>
                        <SelectItem value="AZ">Arizona (AZ)</SelectItem>
                        <SelectItem value="AR">Arkansas (AR)</SelectItem>
                        <SelectItem value="CA">California (CA)</SelectItem>
                        <SelectItem value="CO">Colorado (CO)</SelectItem>
                        <SelectItem value="CT">Connecticut (CT)</SelectItem>
                        <SelectItem value="DE">Delaware (DE)</SelectItem>
                        <SelectItem value="FL">Florida (FL)</SelectItem>
                        <SelectItem value="GA">Georgia (GA)</SelectItem>
                        <SelectItem value="HI">Hawaii (HI)</SelectItem>
                        <SelectItem value="ID">Idaho (ID)</SelectItem>
                        <SelectItem value="IL">Illinois (IL)</SelectItem>
                        <SelectItem value="IN">Indiana (IN)</SelectItem>
                        <SelectItem value="IA">Iowa (IA)</SelectItem>
                        <SelectItem value="KS">Kansas (KS)</SelectItem>
                        <SelectItem value="KY">Kentucky (KY)</SelectItem>
                        <SelectItem value="LA">Louisiana (LA)</SelectItem>
                        <SelectItem value="ME">Maine (ME)</SelectItem>
                        <SelectItem value="MD">Maryland (MD)</SelectItem>
                        <SelectItem value="MA">Massachusetts (MA)</SelectItem>
                        <SelectItem value="MI">Michigan (MI)</SelectItem>
                        <SelectItem value="MN">Minnesota (MN)</SelectItem>
                        <SelectItem value="MS">Mississippi (MS)</SelectItem>
                        <SelectItem value="MO">Missouri (MO)</SelectItem>
                        <SelectItem value="MT">Montana (MT)</SelectItem>
                        <SelectItem value="NE">Nebraska (NE)</SelectItem>
                        <SelectItem value="NV">Nevada (NV)</SelectItem>
                        <SelectItem value="NH">New Hampshire (NH)</SelectItem>
                        <SelectItem value="NJ">New Jersey (NJ)</SelectItem>
                        <SelectItem value="NM">New Mexico (NM)</SelectItem>
                        <SelectItem value="NY">New York (NY)</SelectItem>
                        <SelectItem value="NC">North Carolina (NC)</SelectItem>
                        <SelectItem value="ND">North Dakota (ND)</SelectItem>
                        <SelectItem value="OH">Ohio (OH)</SelectItem>
                        <SelectItem value="OK">Oklahoma (OK)</SelectItem>
                        <SelectItem value="OR">Oregon (OR)</SelectItem>
                        <SelectItem value="PA">Pennsylvania (PA)</SelectItem>
                        <SelectItem value="RI">Rhode Island (RI)</SelectItem>
                        <SelectItem value="SC">South Carolina (SC)</SelectItem>
                        <SelectItem value="SD">South Dakota (SD)</SelectItem>
                        <SelectItem value="TN">Tennessee (TN)</SelectItem>
                        <SelectItem value="TX">Texas (TX)</SelectItem>
                        <SelectItem value="UT">Utah (UT)</SelectItem>
                        <SelectItem value="VT">Vermont (VT)</SelectItem>
                        <SelectItem value="VA">Virginia (VA)</SelectItem>
                        <SelectItem value="WA">Washington (WA)</SelectItem>
                        <SelectItem value="WV">West Virginia (WV)</SelectItem>
                        <SelectItem value="WI">Wisconsin (WI)</SelectItem>
                        <SelectItem value="WY">Wyoming (WY)</SelectItem>
                        <SelectItem value="DC">District of Columbia (DC)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="zip_code">Zip Code *</Label>
                    <Input
                      id="zip_code"
                      value={companyFormData.zip_code}
                      onChange={(e) => handleAddressFieldChange('zip_code', e.target.value)}
                      placeholder="10001"
                      disabled={!isEditingCompany}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Select
                      value={companyFormData.country}
                      onValueChange={(v) => setCompanyFormData(prev => ({ ...prev, country: v }))}
                      disabled={!isEditingCompany}
                    >
                      <SelectTrigger id="country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="United States">United States</SelectItem>
                        <SelectItem value="Canada">Canada</SelectItem>
                        <SelectItem value="Mexico">Mexico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website_url">Website</Label>
                    <Input
                      id="website_url"
                      type="text"
                      value={companyFormData.website_url}
                      onChange={(e) => setCompanyFormData(prev => ({ ...prev, website_url: e.target.value }))}
                      placeholder="example.com"
                      disabled={!isEditingCompany}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accounts_payable_email">Accounts Payable Email *</Label>
                    <Input
                      id="accounts_payable_email"
                      type="email"
                      value={companyFormData.accounts_payable_email}
                      onChange={(e) => setCompanyFormData(prev => ({ ...prev, accounts_payable_email: e.target.value }))}
                      placeholder="ap@yourcompany.com"
                      disabled={!isEditingCompany}
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Invoices will be sent to this email address
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Business Description *</Label>
                  <Textarea
                    id="description"
                    value={companyFormData.description}
                    onChange={(e) => setCompanyFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe your business..."
                    rows={4}
                    disabled={!isEditingCompany}
                    required
                  />
                </div>

                {isOwner && isEditingCompany && (
                  <div className="flex gap-2">
                    <Button type="submit">
                      Update Company Profile
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsEditingCompany(false)}>
                      Cancel
                    </Button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Tax Exemptions Section */}
          {user?.organization_id && showTaxExemptions && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Tax Exemptions</CardTitle>
                <CardDescription>
                  Manage tax exemption certificates for states where your organization is tax-exempt
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TaxExemptionsTab organizationId={user.organization_id} />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Address Suggestion Dialog - only render when lastResult exists */}
      {lastResult && (
        <AddressSuggestionDialog
          open={showSuggestionDialog}
          onOpenChange={setShowSuggestionDialog}
          originalAddress={{
            street: companyFormData.street_address,
            city: companyFormData.city,
            state: companyFormData.state,
            zipcode: companyFormData.zip_code,
          }}
          validationResult={lastResult}
          onSelectAddress={handleSelectSuggestedAddress}
          onKeepOriginal={handleKeepOriginalAddress}
        />
      )}
    </div>
  );
}
